/*
 * Bushnell — cart drawer enhancements:
 * 1. Force PLP (quick-add) add-to-cart to open the cart drawer via AJAX
 *    instead of navigating to the full /cart page. The drawer contents are
 *    refreshed by swapping only the INNER html of the existing
 *    cart-items-component (never the element itself). The theme's delegated
 *    on:click bindings don't reliably attach to AJAX-injected rows, so we bind
 *    remove/quantity directly (bindDrawerControls) and drive /cart/change.js.
 * 2. Instant pending feedback on quantity steppers (.bn-qty-pending).
 * 3. Remove-item undo toast: 5s "Removed <item> — Undo" that re-adds the line.
 *
 * Fail-safe: on any error the original behavior runs natively, so the
 * cart never breaks.
 */
(function () {
  if (window.__bushnellAjaxCart) return;
  window.__bushnellAjaxCart = true;

  var ADD_URL = '/cart/add.js';
  var UNDO_TOAST_MS = 5000;

  function nativeSubmit(form) {
    try { HTMLFormElement.prototype.submit.call(form); }
    catch (e) { window.location.href = '/cart'; }
  }

  /* Open the cart drawer. Prefer the component's own open() method; fall back
     to clicking the header trigger (on:click="/open"). Opening is idempotent. */
  function openDrawer(drawer) {
    drawer = drawer || document.querySelector('cart-drawer-component');
    if (!drawer) return;
    try {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
    } catch (e) {}
    var trigger = drawer.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.click();
  }

  /* Spinner + disabled state on a button without destroying its markup.
     Pairs with the `.is-loading` styles defined in the PDP section. */
  function setBtnLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      try {
        var c = getComputedStyle(btn).color;
        /* If the button already has is-loading (colour is transparent), don't
           overwrite the spinner colour with transparent — fall back to #1c1c1c. */
        if (!c || c === 'transparent' || c.indexOf('rgba(0, 0, 0, 0)') !== -1) c = '#1c1c1c';
        btn.style.setProperty('--bpdp-spin', c);
      } catch (e) {}
      btn.classList.add('is-loading');
      btn.disabled = true;
    } else {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.style.removeProperty('--bpdp-spin');
    }
  }

  /* Refresh the drawer contents from the server.
     CRITICAL: we swap only the INNER html of the existing cart-items-component,
     never the element itself. The element stays a live, upgraded custom element,
     so its declarative on:click (remove) + quantity bindings keep working — and
     the component's MutationObserver rebuilds its `refs` from the new children.
     Replacing the element with a DOMParser node (as before) yields a node that
     is NOT `instanceof Component`, which silently breaks remove/quantity. */
  async function refreshDrawer() {
    var live = document.querySelector('cart-drawer-component cart-items-component');
    var sectionId = live && live.getAttribute('data-section-id');
    if (!sectionId || !live) return false;

    try {
      var res = await fetch('/?section_id=' + encodeURIComponent(sectionId), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return false;

      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');

      var freshItems = doc.querySelector('cart-items-component');
      if (freshItems) {
        live.innerHTML = freshItems.innerHTML;
      }

      // Header cart bubble is a plain element (not a component) — safe to swap.
      var freshBubble = doc.querySelector('.header-actions__cart-icon .cart-bubble');
      var liveBubble = document.querySelector('.header-actions__cart-icon .cart-bubble');
      if (freshBubble && liveBubble && liveBubble.parentNode) {
        liveBubble.parentNode.replaceChild(freshBubble, liveBubble);
      }

      var dialog = document.querySelector('.cart-drawer__dialog');
      if (dialog) dialog.classList.remove('cart-drawer--empty');

      var icon = document.querySelector('.header-actions__cart-icon');
      if (icon) icon.classList.add('header-actions__cart-icon--has-cart');

      // The theme's delegated on:click bindings do not reliably attach to
      // AJAX-injected drawer rows, so drive remove/quantity ourselves.
      bindDrawerControls(live);
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Instantly collapse a row for optimistic feedback (no wait for the server).
     refreshDrawer() re-renders from the server afterwards, so this is undone
     automatically if the change fails. */
  function collapseRow(row) {
    try {
      row.style.overflow = 'hidden';
      row.style.height = row.offsetHeight + 'px';
      void row.offsetHeight; // force reflow so the transition runs
      row.style.transition = 'height .18s ease, opacity .18s ease, margin .18s ease, padding .18s ease, border-color .18s ease';
      row.style.height = '0px';
      row.style.opacity = '0';
      row.style.marginTop = '0';
      row.style.marginBottom = '0';
      row.style.paddingTop = '0';
      row.style.paddingBottom = '0';
      row.style.borderColor = 'transparent';
    } catch (e) {
      row.style.display = 'none';
    }
  }

  /* Update a single cart line by its line-item key, then re-render the drawer.
     Re-renders on failure too, so optimistic UI is reconciled with the server. */
  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { if (!res.ok) throw new Error('change failed'); return res.json(); })
      .then(function () { return refreshDrawer(); })
      .catch(function () { return refreshDrawer(); });
  }

  /* Bind direct remove/quantity handlers to freshly-rendered drawer rows and
     strip the theme's on:click so its delegated listener does not double-fire. */
  function bindDrawerControls(root) {
    root = root || document.querySelector('cart-drawer-component cart-items-component');
    if (!root) return;

    root.querySelectorAll('.cart-items__table-row').forEach(function (row) {
      var key = row.dataset.key;
      if (!key) return;
      if (row.classList.contains('cart-items__nested-line')) return; // bundle child

      var input = row.querySelector('input[name="updates[]"], .quantity-selector input');
      var min = input && input.getAttribute('min') ? parseInt(input.getAttribute('min'), 10) || 1 : 1;
      var step = input && input.getAttribute('step') ? parseInt(input.getAttribute('step'), 10) || 1 : 1;
      var max = input && input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : null;
      function current() { return input ? (parseInt(input.value, 10) || min) : min; }

      var removeBtn = row.querySelector('.cart-items__remove');
      if (removeBtn && !removeBtn._bnBound) {
        removeBtn._bnBound = true;
        removeBtn.removeAttribute('on:click');
        removeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (row._bnRemoving) return;
          row._bnRemoving = true;
          var titleEl = row.querySelector('.cart-items__title');
          showUndoToast({
            variantId: String(key).split(':')[0],
            quantity: current(),
            title: titleEl ? titleEl.textContent.trim() : 'item'
          });
          collapseRow(row);     // hide instantly
          changeLine(key, 0);   // then reconcile with the server
        });
      }

      var minus = row.querySelector('.quantity-minus');
      if (minus && !minus._bnBound) {
        minus._bnBound = true;
        minus.removeAttribute('on:click');
        minus.addEventListener('click', function (e) {
          e.preventDefault();
          var q = current() - step;
          if (q < min) q = min; // removal is via the remove button
          if (q !== current()) changeLine(key, q);
        });
      }

      var plus = row.querySelector('.quantity-plus');
      if (plus && !plus._bnBound) {
        plus._bnBound = true;
        plus.removeAttribute('on:click');
        plus.addEventListener('click', function (e) {
          e.preventDefault();
          var q = current() + step;
          if (max != null && q > max) q = max;
          if (q !== current()) changeLine(key, q);
        });
      }

      if (input && !input._bnBound) {
        input._bnBound = true;
        input.removeAttribute('on:blur');
        input.addEventListener('change', function () {
          var q = parseInt(input.value, 10);
          if (isNaN(q)) return;
          if (q < min) q = min;
          if (max != null && q > max) q = max;
          changeLine(key, q);
        });
      }
    });
  }

  /* ---- 0. Instant loading feedback on click ----
     The submit event fires a moment after the click, so show the spinner right
     away on pointer/click for any add-to-cart submit button. The submit handler
     below then keeps managing it (and removes it after the add / on error). */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var btn = t.closest('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button[name="add"]');
    if (!btn || btn.disabled) return;
    /* Visual spinner only — do NOT disable the button here. A disabled submit
       button cancels the form submission (and the Extend flow), so the product
       would never get added. The submit handler below sets the full loading
       state (incl. disabled) after it preventDefaults.
       Set --bpdp-spin from the current text colour (skip if it's already
       transparent, e.g. the class is somehow still applied) so the ring shows. */
    try {
      var c = getComputedStyle(btn).color;
      if (!c || c === 'transparent' || c.indexOf('rgba(0, 0, 0, 0)') !== -1) c = '#1c1c1c';
      btn.style.setProperty('--bpdp-spin', c);
    } catch (err) {}
    btn.classList.add('is-loading');
  }, true);

  /* ---- 1. PLP add-to-cart → drawer ---- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.matches !== 'function') return;
    if (!form.matches('form[action*="/cart/add"]')) return;

    // Must have a variant id to add directly; otherwise let it pass.
    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value || idField.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    /* Loading feedback on the button that triggered the add */
    var loadingBtn = e.submitter || form.querySelector('[type="submit"]');
    setBtnLoading(loadingBtn, true);

    var data = new FormData(form);

    // PDP add-on products ("Complete Your Setup"): the checkboxes carry no
    // name attribute, so FormData never includes them. Collect checked ones
    // from the surrounding section and switch to a JSON items payload.
    var scope = form.closest('.shopify-section') || document;
    var addonIds = [];
    scope.querySelectorAll('[data-addon-variant-id]:checked').forEach(function (cb) {
      var aid = parseInt(cb.dataset.addonVariantId, 10);
      if (aid && !isNaN(aid)) addonIds.push(aid);
    });

    var fetchOpts;
    if (addonIds.length) {
      var items = [{
        id: parseInt(idField.value, 10),
        quantity: parseInt(data.get('quantity'), 10) || 1
      }];
      addonIds.forEach(function (aid) { items.push({ id: aid, quantity: 1 }); });
      fetchOpts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: items })
      };
    } else {
      fetchOpts = {
        method: 'POST',
        body: data,
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      };
    }

    fetch(ADD_URL, fetchOpts)
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function () {
        var drawer = document.querySelector('cart-drawer-component');
        // No drawer on this page → fall back to the full cart page.
        if (!drawer) {
          window.location.href = '/cart';
          return;
        }
        // Refresh drawer contents (inner-html swap keeps the component alive so
        // remove/quantity keep working), then open the drawer explicitly — the
        // theme's auto-open is gated behind a setting, so never rely on it.
        return refreshDrawer().then(function () {
          setBtnLoading(loadingBtn, false);
          openDrawer(drawer);
        });
      })
      .catch(function () {
        setBtnLoading(loadingBtn, false);
        nativeSubmit(form);
      });
  }, true);

  /* ---- 2. Quantity stepper pending feedback ---- */
  document.addEventListener('click', function (e) {
    try {
      var btn = e.target.closest && e.target.closest('.cart-drawer .quantity-selector button');
      if (!btn) return;
      var selector = btn.closest('.quantity-selector');
      if (!selector) return;
      selector.classList.add('bn-qty-pending');
      setTimeout(function () {
        try { selector.classList.remove('bn-qty-pending'); } catch (err) {}
      }, 1200);
    } catch (err) {}
  }, true);

  /* ---- 3. Remove-item undo toast ---- */
  var toastEl = null;
  var toastTimer = null;
  var lastRemoved = null; // { variantId, quantity, title }

  function getToast() {
    var dialog = document.querySelector('.cart-drawer__dialog');
    if (!dialog) return null;
    if (toastEl && dialog.contains(toastEl)) return toastEl;

    toastEl = document.createElement('div');
    toastEl.className = 'bn-undo-toast';
    toastEl.setAttribute('role', 'status');

    var text = document.createElement('span');
    text.className = 'bn-undo-toast__text';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bn-undo-toast__btn';
    btn.textContent = 'Undo';
    btn.addEventListener('click', function () {
      var item = lastRemoved;
      hideToast();
      if (!item) return;
      fetch(ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: item.variantId, quantity: item.quantity })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('undo failed');
          return res.json();
        })
        .then(function () { return refreshDrawer(); })
        .catch(function () {});
    });

    toastEl.appendChild(text);
    toastEl.appendChild(btn);
    dialog.appendChild(toastEl);
    return toastEl;
  }

  function hideToast() {
    clearTimeout(toastTimer);
    lastRemoved = null;
    if (toastEl) toastEl.classList.remove('bn-undo-toast--visible');
  }

  function showUndoToast(item) {
    var el = getToast();
    if (!el) return;
    lastRemoved = item;
    el.querySelector('.bn-undo-toast__text').textContent = 'Removed ' + item.title;
    el.classList.add('bn-undo-toast--visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, UNDO_TOAST_MS);
  }

  // Capture the line data before Horizon's own handler removes it.
  // Skips rows we control directly (bindDrawerControls) — those show their own
  // toast and perform the removal, so this listener must not double-fire.
  document.addEventListener('click', function (e) {
    try {
      var btn = e.target.closest && e.target.closest('.cart-drawer .cart-items__remove');
      if (!btn || btn._bnBound) return;

      var row = btn.closest('.cart-items__table-row');
      if (!row || !row.dataset.key) return;
      if (row.classList.contains('cart-items__nested-line')) return; // bundles: skip

      var variantId = String(row.dataset.key).split(':')[0];
      if (!variantId) return;

      var qtyInput = row.querySelector('.quantity-selector input');
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      var titleEl = row.querySelector('.cart-items__title');
      var title = titleEl ? titleEl.textContent.trim() : 'item';

      showUndoToast({ variantId: variantId, quantity: quantity, title: title });
    } catch (err) {}
  }, true);
})();
